import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, X, Printer } from "lucide-react";
import { toast } from "sonner";
import InvoicePrint from "@/components/InvoicePrint";
import { useCurrency } from "@/context/CurrencyContext";

const STATUS_LABEL = { pa_paguar: "Pa paguar", pjeserisht: "Pjesërisht", paguar: "Paguar" };
const STATUS_COLOR = {
  pa_paguar: "bg-[#9E3C3C]/15 text-[#9E3C3C]",
  pjeserisht: "bg-[#EAE4D9] text-[#2C4C3B]",
  paguar: "bg-[#2C4C3B] text-[#FDFBF7]",
};

export default function Billing() {
  const { format, symbol } = useCurrency();
  const [list, setList] = useState([]);
  const [patients, setPatients] = useState([]);
  const [open, setOpen] = useState(false);
  const [printing, setPrinting] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(null);
  const [tab, setTab] = useState("invoice"); // "invoice" | "proforma"
  const [form, setForm] = useState({
    patient_id: "", items: [{ description: "", quantity: 1, unit_price: 0 }],
    discount: 0, paid: 0, notes: "", currency: "ALL", is_proforma: false,
  });

  const load = async () => {
    const [i, p] = await Promise.all([api.get("/invoices"), api.get("/patients")]);
    setList(i.data); setPatients(p.data);
  };
  const loadExchange = async () => {
    try {
      const r = await api.get("/exchange-rate");
      setExchangeRate(r.data);
    } catch (e) {
      // ignore exchange rate fetch failures
    }
  };
  useEffect(() => {
    Promise.all([
      api.get("/invoices"),
      api.get("/patients"),
      api.get("/exchange-rate").catch(() => ({ data: null })),
    ]).then(([i, p, ex]) => {
      setList(i.data);
      setPatients(p.data);
      if (ex.data) setExchangeRate(ex.data);
    });
  }, []);

  const patientName = (id) => {
    const p = patients.find((x) => x.id === id);
    return p ? `${p.first_name} ${p.last_name}` : "—";
  };

  const subtotal = form.items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
  const total = Math.max(0, subtotal - (Number(form.discount) || 0));

  const updateItem = (idx, field, val) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: field === "description" ? val : Number(val) };
    setForm({ ...form, items });
  };
  const addItem = () => setForm({ ...form, items: [...form.items, { description: "", quantity: 1, unit_price: 0 }] });
  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/invoices", {
        ...form,
        items: form.items.map((i) => ({
          description: i.description, quantity: Number(i.quantity), unit_price: Number(i.unit_price),
        })),
        discount: Number(form.discount), paid: Number(form.paid),
        status: "pa_paguar",
        currency: form.currency,
        is_proforma: form.is_proforma,
      });
      toast.success(form.is_proforma ? "Proforma u krijua" : "Fatura u krijua");
      setOpen(false);
      setForm({ patient_id: "", items: [{ description: "", quantity: 1, unit_price: 0 }], discount: 0, paid: 0, notes: "", currency: "ALL", is_proforma: false });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const fmtMoney = (val, curr) => {
    const n = Number(val) || 0;
    if (curr === "EUR") return `€${n.toFixed(2)}`;
    if (curr === "ALL") return `${n.toLocaleString("sq-AL", { maximumFractionDigits: 0 })} Lek`;
    return format(n);
  };

  const remove = async (id) => {
    if (!window.confirm("Fshini këtë faturë?")) return;
    try {
      await api.delete(`/invoices/${id}`);
      toast.success("Fatura u fshi");
      load();
    } catch {
      toast.error("Nuk keni leje");
    }
  };

  return (
    <div className="space-y-8" data-testid="billing-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">Financa</div>
          <h1 className="mt-2 text-4xl font-semibold tracking-tight sm:text-5xl">Faturimi</h1>
          <p className="mt-2 text-sm text-[#5C5C5C]">Krijoni dhe ndiqni faturat e pacientëve.</p>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="btn-primary" data-testid="add-invoice-button">
              <Plus size={16} /> Faturë e Re
            </button>
          </DialogTrigger>
          <DialogContent className="bg-white max-w-2xl">
            <DialogHeader>
              <DialogTitle>{form.is_proforma ? "Krijo Proformë" : "Krijo Faturë"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2">
              {/* Type toggle: invoice vs proforma */}
              <div className="grid grid-cols-2 gap-2">
                <button type="button"
                        onClick={() => setForm({ ...form, is_proforma: false })}
                        data-testid="type-invoice"
                        className={`rounded-lg border p-3 text-sm font-medium transition-colors ${
                          !form.is_proforma ? "border-[#2C4C3B] bg-[#2C4C3B] text-white" : "border-[#E2DFD8] text-[#5C5C5C]"
                        }`}>
                  📄 Faturë
                </button>
                <button type="button"
                        onClick={() => setForm({ ...form, is_proforma: true })}
                        data-testid="type-proforma"
                        className={`rounded-lg border p-3 text-sm font-medium transition-colors ${
                          form.is_proforma ? "border-[#D4A24C] bg-[#D4A24C]/15 text-[#8B6914]" : "border-[#E2DFD8] text-[#5C5C5C]"
                        }`}>
                  📋 Proformë (parashikim)
                </button>
              </div>
              {form.is_proforma && (
                <p className="text-xs text-[#8B6914] bg-[#D4A24C]/10 border border-[#D4A24C]/30 rounded p-2">
                  Proforma është një parashikim i kostove. Nuk regjistrohet si fitim derisa të konvertohet në faturë reale.
                </p>
              )}

              <div>
                <Label className="label-eyebrow">Pacienti</Label>
                <Select value={form.patient_id} onValueChange={(v) => setForm({ ...form, patient_id: v })}>
                  <SelectTrigger className="mt-2" data-testid="invoice-patient-select"><SelectValue placeholder="Zgjidh pacientin" /></SelectTrigger>
                  <SelectContent>
                    {patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="label-eyebrow">Zërat</Label>
                  <button type="button" onClick={addItem} className="text-sm text-[#2C4C3B] hover:underline">+ Shto zë</button>
                </div>
                <div className="space-y-2">
                  {form.items.map((it, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                      <Input className="col-span-6" placeholder="Përshkrim" value={it.description}
                             onChange={(e) => updateItem(idx, "description", e.target.value)} required data-testid={`item-desc-${idx}`} />
                      <Input className="col-span-2" type="number" min="1" placeholder="Sasia" value={it.quantity}
                             onChange={(e) => updateItem(idx, "quantity", e.target.value)} required />
                      <Input className="col-span-3" type="number" step="0.01" placeholder="Çmimi" value={it.unit_price}
                             onChange={(e) => updateItem(idx, "unit_price", e.target.value)} required />
                      <button type="button" onClick={() => removeItem(idx)} className="col-span-1 text-[#9E3C3C]">
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="label-eyebrow">Monedha</Label>
                  <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                    <SelectTrigger className="mt-2" data-testid="invoice-currency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">Lek (ALL)</SelectItem>
                      <SelectItem value="EUR">Euro (€)</SelectItem>
                    </SelectContent>
                  </Select>
                  {exchangeRate && (
                    <p className="text-[10px] text-[#5C5C5C] mt-1">
                      1 EUR = {exchangeRate.eur_to_all_rate} Lek
                    </p>
                  )}
                </div>
                <div>
                  <Label className="label-eyebrow">Zbritje ({form.currency === "EUR" ? "€" : "Lek"})</Label>
                  <Input type="number" step="0.01" value={form.discount}
                         onChange={(e) => setForm({ ...form, discount: e.target.value })} className="mt-2" />
                </div>
                <div>
                  <Label className="label-eyebrow">Paguar ({form.currency === "EUR" ? "€" : "Lek"})</Label>
                  <Input type="number" step="0.01" value={form.paid} disabled={form.is_proforma}
                         onChange={(e) => setForm({ ...form, paid: e.target.value })} className="mt-2" />
                </div>
              </div>

              <div className="rounded-lg bg-[#EAE4D9]/50 p-4 flex justify-between items-center">
                <div className="text-sm text-[#5C5C5C]">{form.is_proforma ? "Parashikimi" : "Totali"}</div>
                <div className="text-2xl font-semibold text-[#2C4C3B] font-mono">{fmtMoney(total, form.currency)}</div>
              </div>

              <DialogFooter>
                <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>Anulo</button>
                <button type="submit" className="btn-primary" data-testid="invoice-save-button">
                  {form.is_proforma ? "Krijo Proformën" : "Krijo Faturën"}
                </button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-1 flex-wrap" data-testid="billing-tabs">
        {[
          { k: "all", label: "Të gjitha" },
          { k: "invoice", label: "📄 Fatura" },
          { k: "proforma", label: "📋 Proforma" },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                    tab === t.k ? "bg-[#2C4C3B] text-[#FDFBF7]"
                                : "bg-white border border-[#E2DFD8] text-[#5C5C5C] hover:text-[#2C4C3B]"
                  }`}>{t.label}</button>
        ))}
      </div>

      <div className="surface-card overflow-hidden">
        {(() => {
          const filtered = list.filter(inv => {
            if (tab === "invoice") return !inv.is_proforma;
            if (tab === "proforma") return !!inv.is_proforma;
            return true;
          });
          return filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#5C5C5C]">Asgjë në këtë seksion.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#FDFBF7] text-left">
              <tr>
                <th className="px-6 py-3 label-eyebrow">Numri</th>
                <th className="px-6 py-3 label-eyebrow">Pacienti</th>
                <th className="px-6 py-3 label-eyebrow">Data</th>
                <th className="px-6 py-3 label-eyebrow">Lloji</th>
                <th className="px-6 py-3 label-eyebrow">Status</th>
                <th className="px-6 py-3 label-eyebrow text-right">Totali</th>
                <th className="px-6 py-3 label-eyebrow text-right">Paguar</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((inv) => (
                <tr key={inv.id} className="border-t border-[#E2DFD8]" data-testid={`invoice-${inv.id}`}>
                  <td className="px-6 py-4 font-mono">{inv.invoice_number}</td>
                  <td className="px-6 py-4">{patientName(inv.patient_id)}</td>
                  <td className="px-6 py-4">{new Date(inv.created_at).toLocaleDateString("sq-AL")}</td>
                  <td className="px-6 py-4">
                    {inv.is_proforma
                      ? <span className="rounded-full bg-[#D4A24C]/15 text-[#8B6914] px-2.5 py-0.5 text-[11px] font-medium">📋 Proformë</span>
                      : <span className="rounded-full bg-[#2C4C3B]/10 text-[#2C4C3B] px-2.5 py-0.5 text-[11px] font-medium">📄 Faturë</span>}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`rounded-full px-3 py-1 text-xs font-medium ${STATUS_COLOR[inv.status]}`}>
                      {STATUS_LABEL[inv.status]}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right font-mono">{fmtMoney(inv.total, inv.currency || "ALL")}</td>
                  <td className="px-6 py-4 text-right font-mono">{fmtMoney(inv.paid, inv.currency || "ALL")}</td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setPrinting(inv)} className="p-2 rounded-full hover:bg-[#EAE4D9]" title="Printo/PDF" data-testid={`print-${inv.id}`}>
                        <Printer size={16} />
                      </button>
                      <button onClick={() => remove(inv.id)} className="text-[#9E3C3C] hover:bg-[#9E3C3C]/10 p-2 rounded-full">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );
        })()}
      </div>

      {printing && (
        <InvoicePrint
          invoice={printing}
          patient={patients.find((p) => p.id === printing.patient_id)}
          onClose={() => setPrinting(null)}
        />
      )}
    </div>
  );
}
